import { FormModal, SubmitProps } from '@/components/form-modal';
import { useToast } from '@/components/ui/hooks/use-toast';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import type { buttonVariants } from '@/components/ui/button';
import { bulkUpdateStudentProfiles, getStudentsCount } from '@/lib/api-calls';
import { partitionBy } from '@airreading/common';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  GetStudentsCountModifier,
  StudentStatus,
  UpdateStudentOptions,
  UPDATE_STUDENTS_BULK_ACTIONS,
  UpdateStudentsBulkActions,
} from '@airreading/common/data';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { VariantProps } from 'class-variance-authority';
import { z } from 'zod';

export interface StudentBulkUpdateEligible {
  id: string;
  status: StudentStatus | null;
}

export interface StudentBulkUpdateProps {
  confirmMessage: string;
  invalidMessage?: string;
  options: UpdateStudentOptions;
  onSuccess?: () => void;
  students: StudentBulkUpdateEligible[];
  title: string;
  validation: (student: StudentBulkUpdateEligible) => boolean;
  isSelectAllPagesActive?: boolean;
  filters?: GetStudentsCountModifier['filters'];
  actionName?: UpdateStudentsBulkActions;
  submitButtonVariant?: VariantProps<typeof buttonVariants>['variant'];
}

const formSchema = z.object({});

export const StudentBulkUpdate = ({
  confirmMessage,
  invalidMessage,
  options,
  onSuccess,
  students,
  title,
  validation,
  isSelectAllPagesActive = false,
  filters,
  actionName,
  submitButtonVariant = 'destructive',
}: StudentBulkUpdateProps) => {
  const { toast } = useToast();
  const { mutateAsync } = useMutation({ mutationFn: bulkUpdateStudentProfiles });
  const [validStudents] = partitionBy(students, validation);

  // Use the new endpoint when selecting all pages
  const { data: studentsCountData } = useQuery({
    ...getStudentsCount({
      filters: {
        ...filters,
        ...(actionName ? UPDATE_STUDENTS_BULK_ACTIONS[actionName].eligibility : {}),
      },
    }),
    enabled: isSelectAllPagesActive && !!filters,
  });

  // Determine counts based on whether all pages are selected
  const totalCount = isSelectAllPagesActive && studentsCountData ? studentsCountData.total : students.length;
  const validCount =
    isSelectAllPagesActive && studentsCountData ? studentsCountData.totalMatching : validStudents.length;
  const invalidCount = totalCount - validCount;

  const buildUpdatePayload = () => {
    if (isSelectAllPagesActive && filters) {
      return { filters, options, ...(actionName ? { actionName } : {}) };
    }
    return { studentIds: validStudents.map(({ id }) => id), options, ...(actionName ? { actionName } : {}) };
  };

  const onSubmit = ({ closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    // Use filters when selecting all pages, otherwise use student IDs
    const updatePayload = buildUpdatePayload();

    return mutateAsync(updatePayload)
      .then(() => {
        toast({
          title: `${validCount} student(s) were updated`,
        });
        onSuccess?.();
        closeModal();
      })
      .catch((e) => {
        toast({
          title: 'There was an error. Please try again.',
          description: airServerErrorFrom(e)?.message,
          variant: 'destructive',
        });
      });
  };

  return (
    <FormModal
      modalTitle={title}
      formSchema={formSchema}
      trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>{title}</DropdownMenuItem>}
      onSubmit={onSubmit}
      submitButtonProps={{
        disabled: validCount === 0,
        variant: submitButtonVariant,
      }}
      confirmBtnLabel="Confirm"
    >
      {() => {
        return (
          <div className="my-6">
            {validCount === 0 ? (
              <div>None of the selected students are eligible. {invalidMessage}</div>
            ) : (
              <>
                {invalidCount > 0 && (
                  <div className="mb-4">{`There are ${invalidCount} student(s) that cannot be eligible. ${invalidMessage}`}</div>
                )}
                {validCount > 0 && <div>{`${confirmMessage} ${validCount} student(s)?`}</div>}
              </>
            )}
          </div>
        );
      }}
    </FormModal>
  );
};
